import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  FaTachometerAlt, 
  FaBuilding,
  FaUser,
  FaCog,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

interface StoreKeeperSidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  onLinkClick?: () => void;
}

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const SidebarContainer = styled.aside<{ isOpen: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ isOpen }) => (isOpen ? '250px' : '70px')};
  min-width: ${({ isOpen }) => (isOpen ? '250px' : '70px')};
  max-width: ${({ isOpen }) => (isOpen ? '250px' : '70px')};
  background: linear-gradient(180deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  overflow: hidden;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    width: 250px;
    box-shadow: ${({ isOpen }) => (isOpen ? '4px 0 20px rgba(0, 0, 0, 0.3)' : 'none')};
  }
`;

const SidebarHeader = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? 'space-between' : 'center')};
  padding: 1.5rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.02);
`;

const LogoContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  .logo-text {
    font-weight: 700;
    font-size: 1.2rem;
    color: #F68B21;
    opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
    transition: opacity 0.3s ease;
  }
`;

const Logo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #F68B21;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 1rem;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const LogoFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F68B21;
  color: white;
  font-weight: 700;
  border-radius: 50%;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SidebarMenu = styled.nav`
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const SidebarMenuItem = styled.div<{ isActive: boolean; isOpen: boolean }>`
  margin: 0.25rem 1rem;
  
  a {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    color: white;
    text-decoration: none;
    border-radius: 8px;
    transition: all 0.2s ease;
    background: ${({ isActive }) => isActive ? 'rgba(246, 139, 33, 0.2)' : 'transparent'};
    border: ${({ isActive }) => isActive ? '1px solid rgba(246, 139, 33, 0.3)' : '1px solid transparent'};
    
    &:hover {
      background: ${({ isActive }) => isActive ? 'rgba(246, 139, 33, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
      transform: translateX(2px);
    }
    
    .menu-icon {
      font-size: 1.1rem;
      color: ${({ isActive }) => isActive ? '#F68B21' : 'rgba(255, 255, 255, 0.8)'};
      margin-right: 0.75rem;
      min-width: 20px;
    }
    
    .menu-text {
      font-weight: 500;
      opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
      transition: opacity 0.3s ease;
      white-space: nowrap;
    }
  }
`;

const SidebarFooter = styled.div<{ isOpen: boolean }>`
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.1);
  
  .developer-info {
    text-align: ${({ isOpen }) => (isOpen ? 'left' : 'center')};
    opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
    transition: all 0.3s ease;
  }
  
  .developer-name {
    font-weight: 600;
    font-size: 0.8rem;
    color: #F68B21;
    margin-bottom: 0.25rem;
  }
  
  .developer-tagline {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }
  
  .copyright {
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 0.25rem;
  }
`;

const StoreKeeperSidebar: React.FC<StoreKeeperSidebarProps> = ({ isOpen, toggleSidebar, onLinkClick }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [logoError, setLogoError] = React.useState(false);
  
  const handleLogoError = () => {
    setLogoError(true);
  };
  
  // Check if the current path matches the menu item path
  const isActive = (path: string) => {
    if (path === '/storekeeper/dashboard' && location.pathname === '/storekeeper/dashboard') {
      return true;
    }
    return location.pathname.startsWith(path) && path !== '/storekeeper/dashboard';
  };
  
  // Define menu items specifically for Store Keeper
  const menuItems = [
    { 
      path: '/storekeeper/dashboard', 
      icon: <IconWrapper icon={FaTachometerAlt} className="menu-icon" />, 
      text: 'Dashboard' 
    },
    { 
      path: '/storekeeper/office', 
      icon: <IconWrapper icon={FaBuilding} className="menu-icon" />, 
      text: 'Office Management' 
    },
    { 
      path: '/storekeeper/profile', 
      icon: <IconWrapper icon={FaUser} className="menu-icon" />, 
      text: 'Profile' 
    },
    { 
      path: '/storekeeper/settings', 
      icon: <IconWrapper icon={FaCog} className="menu-icon" />, 
      text: 'Settings' 
    }
  ];

  const handleLinkClick = () => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  return (
    <SidebarContainer isOpen={isOpen}>
      <SidebarHeader isOpen={isOpen}>
        <LogoContainer isOpen={isOpen}>
          <Logo>
            {!logoError ? (
              <LogoImage 
                src="/pfrs.jpeg" 
                alt="PFRS Logo" 
                onError={handleLogoError}
              />
            ) : (
              <LogoFallback>P</LogoFallback>
            )}
          </Logo>
          <span className="logo-text">Store Keeper</span>
        </LogoContainer>
        <ToggleButton onClick={toggleSidebar}>
          {isOpen ? <IconWrapper icon={FaChevronLeft} /> : <IconWrapper icon={FaChevronRight} />}
        </ToggleButton>
      </SidebarHeader>
      
      <SidebarMenu>
        {menuItems.map((item, index) => (
          <SidebarMenuItem key={index} isActive={isActive(item.path)} isOpen={isOpen}>
            <Link to={item.path} onClick={handleLinkClick}>
              {item.icon}
              <span className="menu-text">{item.text}</span>
            </Link>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
      
      <SidebarFooter isOpen={isOpen}>
        <div className="developer-info">
          <div className="developer-name">Store Keeper Portal</div>
          <div className="developer-tagline">Office Management System</div>
          <div className="copyright">
            &copy; {new Date().getFullYear()} PFRS
          </div>
        </div>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default StoreKeeperSidebar; 